import { useEffect, useState } from 'react'
import { apiURL } from '../../../../utilities/URLs'
import { account } from '../../../../classes/Account'
import { refreshAccessToken } from '../../../../utilities/extensions'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, Check } from 'lucide-react'
import { Progress } from '../classes/Progress'
import DashboardFAQTile from './DashboardTiles/DashboardFAQTile'
import DashboardOnlineAgentTile from './DashboardTiles/DashboardOnlineAgentTile'
import DashboardProgressTile from './DashboardTiles/DashboardProgressTile'
import IrsCorrespondenceUpload from './IrsCorrespondenceUpload'

const CLAIM_PROCESSING_STAGE_ID = '7a41d0b2-1369-4005-8bc9-d040b3e13b87'
const MOBILE_BREAKPOINT = 640

export default function DashboardProgressTracker({ applicationId, stageId, hasActiveUploadWorkflow }) {
  const navigate = useNavigate()
  const [dashboardProgressTracker, setDashboardProgressTracker] = useState([])
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT)

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    const handleGetDashboardProgressTracker = async () => {
      const dashboardProgressTrackerResponse = await fetch(`${apiURL}/api/applications/${applicationId}/dashboard/progress-tracker/`, {
        method: 'GET',
        headers: {
          'Authorization': account.accessToken
        }
      })
      const dashboardProgressTrackerData = await dashboardProgressTrackerResponse.json()
      if (dashboardProgressTrackerData.response.error) {
        if (dashboardProgressTrackerData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? await handleGetDashboardProgressTracker() : navigate('/', { state: { expired: true } })
        } else
          console.error(dashboardProgressTrackerData.response.error)
      } else
        setDashboardProgressTracker(dashboardProgressTrackerData.response.progressTracker)
    }

    handleGetDashboardProgressTracker()
  }, [applicationId, navigate])

  return (
    <div className='w-full flex flex-1 flex-col text-slate-900 dark:text-white transition-colors duration-300'>
      <p className='text-2xl font-semibold'>Progress Tracker</p>
      <div className='w-full flex items-start justify-between mt-7 overflow-x-auto'>
        {(() => {
          const currentIndex = dashboardProgressTracker.findIndex(p => p.isCurrent === 1)
          const activeIndex = currentIndex === -1 ? 0 : currentIndex

          // On mobile: window to 3 steps around the active step
          // On desktop: show all steps
          let startIndex = 0
          let endIndex = dashboardProgressTracker.length - 1

          if (isMobile) {
            startIndex = activeIndex - 1
            endIndex = activeIndex + 1

            if (startIndex < 0) {
              startIndex = 0
              endIndex = Math.min(2, dashboardProgressTracker.length - 1)
            } else if (endIndex >= dashboardProgressTracker.length) {
              endIndex = dashboardProgressTracker.length - 1
              startIndex = Math.max(0, dashboardProgressTracker.length - 3)
            }
          }

          return dashboardProgressTracker.map((_progress, originalIndex) => {
            if (originalIndex < startIndex || originalIndex > endIndex) return null

            const progress = new Progress(_progress)
            const isCompleted = originalIndex < activeIndex
            const isFirstVisible = originalIndex === startIndex
            const isLastVisible = originalIndex === endIndex

            return (
              <div key={originalIndex} className='flex flex-1 flex-col items-center relative'>
                {!isFirstVisible && (
                  <div className={`absolute top-7 left-0 w-1/2 h-1 ${(isCompleted || (progress.isCurrent && (progress.icon === 'alert' || progress.icon === 'check'))) ? 'bg-[#B6D7A9]' : 'bg-gray-200 dark:bg-slate-700'}`} />
                )}
                {!isLastVisible && (
                  <div className={`absolute top-7 right-0 w-1/2 h-1 ${isCompleted ? 'bg-[#B6D7A9]' : 'bg-gray-200 dark:bg-slate-700'}`} />
                )}
                <div className={`w-14 h-14 z-10 rounded-full flex items-center justify-center text-white text-xl font-bold border-2 border-slate-200 dark:border-slate-700 ${progress.isCurrent ? (progress.icon === 'alert' ? 'bg-yellow-500 shadow-lg border-yellow-500' : (progress.icon === 'exclamation' ? 'bg-white dark:bg-slate-800 shadow-lg' : 'bg-green-500 shadow-lg border-green-500')) : isCompleted ? 'bg-[#B6D7A9] border-[#B6D7A9] shadow-sm' : 'bg-white dark:bg-slate-800'}`}>
                  {isCompleted ? (
                    <Check className='text-ertc-secondary' size={36} />
                  ) : (
                    progress.isCurrent && (
                      progress.icon === 'alert' ? (
                        <AlertTriangle size={36} />
                      ) : progress.icon === 'exclamation' ? (
                        <p>❗</p>
                      ) : (
                        <Check size={36} />
                      )
                    )
                  )}
                </div>
                <p className='mt-3 text-center font-medium text-slate-700 dark:text-slate-300 text-sm max-w-[120px]'>{progress.name}</p>
              </div>
            )
          })
        })()}
      </div>
      {stageId === CLAIM_PROCESSING_STAGE_ID && !hasActiveUploadWorkflow && (
        <IrsCorrespondenceUpload applicationId={applicationId} />
      )}
      <div className='w-full flex max-sm:flex-col gap-4 mt-7'>
        <DashboardProgressTile description={dashboardProgressTracker.find(p => p.isCurrent === 1)?.description ?? ''} />
        <DashboardFAQTile />
        <DashboardOnlineAgentTile applicationId={applicationId} />
      </div>
    </div>
  )
}